import React from 'react';
import { Clock, User, MapPin, AlertCircle, Check } from 'lucide-react';

/**
 * Enhanced Appointment Card Component
 *
 * Shows job-focused appointment information including:
 * - Job title and visit sequence (Visit X/Y)
 * - Treatment address (WHERE the work happens)
 * - Customer name (WHO we're working for)
 * - Time, duration, and technician
 * - Job status and appointment status
 *
 * Props:
 * - appointment: Appointment object with enhanced job data
 * - onClick: Handler for card click
 * - onDragStart: Handler for drag start (optional)
 * - onDragEnd: Handler for drag end (optional)
 * - style: Additional inline styles (for positioning)
 * - className: Additional CSS classes
 * - draggable: Whether card is draggable
 * - compact: Use compact layout for Month view (multi-line, vertical)
 * - minimal: Use minimal layout for Week view (single-line, horizontal)
 * - onPointerDown: Handler for pointer down (touch drag initiation)
 */
const AppointmentCard = ({
  appointment,
  onClick,
  onDragStart,
  onDragEnd,
  onQuickStatus,
  onResizeStart,
  onResizeTouchStart,
  onPointerDown,
  style = {},
  className = '',
  draggable = true,
  compact = false,
  minimal = false,
  isResizing = false,
}) => {
  // Format appointment time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return 'Unknown';
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Format treatment address
  const formatAddress = () => {
    const parts = [];
    if (appointment.treatment_address_line1) parts.push(appointment.treatment_address_line1);
    if (appointment.treatment_address_city) parts.push(appointment.treatment_address_city);
    if (appointment.treatment_address_postcode) parts.push(appointment.treatment_address_postcode);

    if (parts.length === 0) return null;

    // For compact view, show only first line and postcode
    if (compact) {
      const compactParts = [];
      if (appointment.treatment_address_line1) compactParts.push(appointment.treatment_address_line1);
      if (appointment.treatment_address_postcode) compactParts.push(appointment.treatment_address_postcode);
      return compactParts.join(', ');
    }

    return parts.join(', ');
  };

  // Card color based on appointment status
  const getStatusColor = () => {
    switch (appointment.status) {
      case 'confirmed':
        return 'bg-blue-800 text-white border-blue-900';
      case 'completed':
        return 'bg-green-600 text-white border-green-700';
      case 'cancelled':
        return 'bg-gray-400 text-white border-gray-500';
      default: // scheduled
        return 'bg-blue-500 text-white border-blue-600';
    }
  };



  // Quick status button component — cycles: scheduled → confirmed → completed
  const getNextStatus = () => {
    if (appointment.status === 'scheduled') return 'confirmed';
    if (appointment.status === 'confirmed') return 'completed';
    return null;
  };

  const QuickStatusButton = ({ size = 'small' }) => {
    const nextStatus = getNextStatus();
    if (!onQuickStatus || !nextStatus) return null;
    const isLarge = size === 'large';
    const isConfirm = nextStatus === 'confirmed';
    return (
      <button
        className={`${isLarge ? 'absolute top-2 right-2 px-2 py-1 rounded-md' : 'absolute top-0.5 right-0.5 w-5 h-5 rounded-full'} bg-white bg-opacity-40 flex items-center ${isLarge ? 'space-x-1' : ''} justify-center opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-70`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onQuickStatus(appointment, nextStatus); }}
        title={isConfirm ? 'Confirm' : 'Mark Complete'}
      >
        {isConfirm ? (
          <>{isLarge && <Check size={14} />}{isLarge && <span className="text-xs font-medium">Confirm</span>}{!isLarge && <Check size={12} />}</>
        ) : (
          <>{isLarge && <Check size={14} />}{isLarge && <span className="text-xs font-medium">Done</span>}{!isLarge && <Check size={12} />}</>
        )}
      </button>
    );
  };

  const time = formatTime(appointment.scheduled_date);
  const duration = formatDuration(appointment.estimated_duration);
  const address = formatAddress();
  const visitBadge = appointment.visit_number && appointment.total_visits
    ? `Visit ${appointment.visit_number}/${appointment.total_visits}`
    : null;


  // Minimal layout for Week view
  if (minimal) {
    // Extract job title - get everything after the last '>'
    let jobTitle = 'Untitled';
    if (appointment.job_title) {
      // Split by '>' and get the last part
      const parts = appointment.job_title.split('>');
      const lastPart = parts[parts.length - 1].trim();

      // Remove customer name suffix if present (after ' - ')
      jobTitle = lastPart.split(' - ')[0].trim();
    }

    // Get postcode
    const postcode = appointment.treatment_address_postcode || '';

    // Determine if this is a short appointment (30 min or less)
    const isShort = (appointment.estimated_duration || 60) <= 30;

    // Short appointments: compact layout
    if (isShort) {
      return (
        <div
          draggable={draggable}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onPointerDown={onPointerDown}
          onClick={onClick}
          style={style}
          className={`px-2 py-1 rounded-lg overflow-hidden ${getStatusColor()} ring-1 ring-black/15 hover:ring-2 hover:ring-white/60 hover:brightness-110 transition-all flex flex-row items-start ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${className} relative group`}
        >
          <div className="flex flex-col justify-start min-w-0 flex-1">
            <span className="font-medium text-xs leading-tight">
              {jobTitle}
            </span>
            {appointment.customer_name && (
              <div className="text-xs opacity-90">{appointment.customer_name}</div>
            )}
            <div className="text-xs opacity-90">{postcode || 'No postcode'}</div>
            <div className="text-xs opacity-90">{time}</div>
          </div>

          {/* Resize handle (bottom) */}
          {onResizeStart && (
            <div
              data-resize-handle="true"
              className={`absolute bottom-0 left-0 right-0 h-10 cursor-ns-resize flex opacity-100 md:h-4 md:opacity-0 md:group-hover:opacity-100 transition-all items-end justify-center ${isResizing ? 'bg-white bg-opacity-40' : ''}`}
              onPointerDown={(e) => { e.stopPropagation(); if (e.pointerType !== 'touch') onResizeStart(e); }}
              onTouchStart={(e) => { e.stopPropagation(); onResizeTouchStart && onResizeTouchStart(e); }}
            >
              <div className={`w-full h-4 bg-white rounded-b-lg flex items-center justify-center transition-all ${isResizing ? 'bg-opacity-60' : 'bg-opacity-30'}`}>
                <div className={`w-8 h-0.5 bg-white rounded transition-all ${isResizing ? 'opacity-100 w-12 h-1' : 'opacity-60'}`} />
              </div>
            </div>
          )}
        </div>
      );
    }

    // Regular appointments: multi-line layout
    return (
      <div
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onPointerDown={onPointerDown}
        onClick={onClick}
        style={style}
        className={`px-2 py-1.5 rounded-lg overflow-hidden ${getStatusColor()} ring-1 ring-black/15 hover:ring-2 hover:ring-white/60 hover:brightness-110 transition-all flex flex-row items-start ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${className} relative group`}
      >
        <div className="flex flex-col justify-start min-w-0 flex-1">
          <span className="font-medium text-xs leading-tight">
            {jobTitle}
          </span>

          {appointment.customer_name && (
            <div className="text-xs opacity-90">{appointment.customer_name}</div>
          )}

          <div className="text-xs opacity-90">
            {postcode || 'No postcode'}
          </div>

          <div className="text-xs opacity-90">
            {time}
          </div>
        </div>

        {/* Resize handle (bottom) */}
        {onResizeStart && (
          <div
            data-resize-handle="true"
            className={`absolute bottom-0 left-0 right-0 h-10 cursor-ns-resize flex opacity-100 md:h-4 md:opacity-0 md:group-hover:opacity-100 transition-all items-end justify-center ${isResizing ? 'bg-white bg-opacity-40' : ''}`}
            onPointerDown={(e) => { e.stopPropagation(); if (e.pointerType !== 'touch') onResizeStart(e); }}
            onTouchStart={(e) => { e.stopPropagation(); onResizeTouchStart && onResizeTouchStart(e); }}
          >
            <div className={`w-full h-4 bg-white rounded-b-lg flex items-center justify-center transition-all ${isResizing ? 'bg-opacity-60' : 'bg-opacity-30'}`}>
              <div className={`w-8 h-0.5 bg-white rounded transition-all ${isResizing ? 'opacity-100 w-12 h-1' : 'opacity-60'}`} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Compact layout for Month view (multi-line, vertical)
  if (compact) {
    return (
      <div
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onPointerDown={onPointerDown}
        onClick={onClick}
        style={style}
        className={`p-2 rounded-lg cursor-pointer transition-all border-l-4 ${getStatusColor()} hover:shadow-lg ${className} relative group`}
      >
        <div className="font-semibold text-xs mb-1 line-clamp-1">
          {appointment.job_title || 'Untitled Job'}
        </div>
        <div className="text-xs opacity-90 line-clamp-1">
          {appointment.customer_name || 'No customer'}
        </div>
        <div className="flex items-center text-xs opacity-90 mt-1">
          <Clock size={10} className="mr-1" />
          {time}
        </div>
        {visitBadge && (
          <div className="text-xs font-semibold mt-1 bg-white bg-opacity-30 px-1 py-0.5 rounded inline-block">
            {visitBadge}
          </div>
        )}
      </div>
    );
  }

  // Full layout for Day view
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onPointerDown={onPointerDown}
      onClick={onClick}
      style={style}
      className={`p-3 rounded-lg cursor-move hover:shadow-xl transition-all border-l-4 ${getStatusColor()} z-10 ${className} relative group`}
    >
      {/* Header: Job Title + Visit Badge + Quick Status */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm mb-1 line-clamp-1" title={appointment.job_title}>
            {appointment.job_title || 'Untitled Job'}
          </div>
          <div className="text-xs opacity-90 line-clamp-1" title={appointment.customer_name}>
            {appointment.customer_name || 'No customer'}
          </div>
        </div>
        <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
          {visitBadge && (
            <div className="text-xs font-semibold bg-white bg-opacity-30 px-2 py-1 rounded">
              {visitBadge}
            </div>
          )}
          {onQuickStatus && getNextStatus() && (
            <button
              className="px-2 py-1 rounded-md bg-white bg-opacity-30 flex items-center space-x-1 text-xs font-medium opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-50"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onQuickStatus(appointment, getNextStatus()); }}
              title={getNextStatus() === 'confirmed' ? 'Confirm' : 'Mark Complete'}
            >
              {getNextStatus() === 'confirmed' ? (
                <><Check size={14} /><span>Confirm</span></>
              ) : (
                <><Check size={14} /><span>Done</span></>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Treatment Address */}
      {address && (
        <div className="flex items-start text-xs opacity-90 mb-2">
          <MapPin size={12} className="mr-1 flex-shrink-0 mt-0.5" />
          <span className="line-clamp-2" title={address}>{address}</span>
        </div>
      )}

      {/* Time and Duration */}
      <div className="flex items-center space-x-3 text-xs opacity-90 mb-2">
        <div className="flex items-center">
          <Clock size={12} className="mr-1" />
          {time}
        </div>
        <div className="flex items-center">
          <Clock size={12} className="mr-1" />
          {duration}
        </div>
      </div>

      {/* Technician */}
      {appointment.technician_name && (
        <div className="flex items-center text-xs opacity-90 mb-2">
          <User size={12} className="mr-1" />
          {appointment.technician_name}
        </div>
      )}

      {/* Job Status Badge (if not active) */}
      {appointment.job_status && appointment.job_status !== 'active' && (
        <div className="flex items-center text-xs mb-2">
          <AlertCircle size={12} className="mr-1 text-yellow-300" />
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-white bg-opacity-30">
            Job: {appointment.job_status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      )}

      {/* Pre-visit Notes (for longer appointments) */}
      {appointment.notes && appointment.estimated_duration && appointment.estimated_duration >= 60 && (
        <div className="mt-2 text-xs opacity-90 bg-white bg-opacity-20 p-2 rounded line-clamp-3">
          {appointment.notes}
        </div>
      )}


    </div>
  );
};

export default AppointmentCard;
